import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../services/api";
import "./BuyerProfile.css";

const PHONE_REGEX = /^\+?\d{7,15}$/;
const CARD_NUMBER_REGEX = /^\d{16}$/;
const EXPIRY_REGEX = /^(0[1-9]|1[0-2])\/(\d{2})$/;
const LOCAL_COUNTRY = "Brunei Darussalam";
const DISTRICT_OPTIONS = ["Brunei Muara", "Tutong", "Belait", "Temburong"];

const emptyAddress = {
  label: "Home",
  receiverName: "",
  phone: "",
  streetLine1: "",
  streetLine2: "",
  city: "",
  state: "",
  postalCode: "",
};

const emptyCard = {
  cardNumber: "",
  cardName: "",
  expiryDate: "",
};

const emptySavedCard = {
  last4: "",
  cardName: "",
  expiryDate: "",
};

const normalizePhone = (value) => value.replace(/[^\d+]/g, "").trim();
const normalizeCardNumber = (value) => value.replace(/\D/g, "").slice(0, 16);
const normalizeExpiry = (value) => {
  const raw = value.replace(/[^\d]/g, "").slice(0, 4);
  if (raw.length <= 2) {
    return raw;
  }
  return `${raw.slice(0, 2)}/${raw.slice(2)}`;
};

const splitStreetAddress = (street = "") => {
  const raw = String(street || "").trim();
  if (!raw) {
    return { streetLine1: "", streetLine2: "" };
  }
  const parts = raw.split(" | ");
  if (parts.length > 1) {
    return {
      streetLine1: (parts[0] || "").trim(),
      streetLine2: parts.slice(1).join(" | ").trim(),
    };
  }
  return { streetLine1: raw, streetLine2: "" };
};

const composeStreetAddress = (streetLine1 = "", streetLine2 = "") =>
  [streetLine1.trim(), streetLine2.trim()].filter(Boolean).join(" | ");

const isExpiryFutureOrCurrent = (expiry) => {
  const match = EXPIRY_REGEX.exec(expiry);
  if (!match) {
    return false;
  }
  const month = Number(match[1]);
  const year = Number(`20${match[2]}`);
  const now = new Date();
  const nowMonth = now.getMonth() + 1;
  const nowYear = now.getFullYear();
  return year > nowYear || (year === nowYear && month >= nowMonth);
};

const validateProfileForm = (form) => {
  const errors = {};
  if (!form.name || !form.name.trim()) {
    errors.name = "Full name is required";
  } else if (form.name.length < 3) {
    errors.name = "Full name must be at least 3 characters";
  }
  const phone = normalizePhone(form.phone);
  if (!phone) {
    errors.phone = "Phone number is required";
  } else if (!PHONE_REGEX.test(phone)) {
    errors.phone = "Phone must be 7-15 digits (optional leading +)";
  }
  return errors;
};

const validateAddressForm = (addressForm) => {
  const errors = {};
  if (!addressForm.label.trim()) {
    errors.label = "Label is required";
  }
  if (!addressForm.receiverName.trim()) {
    errors.receiverName = "Receiver name is required";
  }
  const phone = normalizePhone(addressForm.phone);
  if (!phone) {
    errors.phone = "Phone number is required";
  } else if (!PHONE_REGEX.test(phone)) {
    errors.phone = "Phone must be 7-15 digits (optional leading +)";
  }
  if (!addressForm.streetLine1.trim()) {
    errors.streetLine1 = "Street Address 1 is required";
  }
  if (!addressForm.city.trim()) {
    errors.city = "City is required";
  }
  if (!addressForm.state.trim()) {
    errors.state = "State is required";
  }
  if (!addressForm.postalCode.trim()) {
    errors.postalCode = "Postal code is required";
  }
  return errors;
};

const validateCardForm = (paymentDetails) => {
  const errors = {};
  const cardNumber = normalizeCardNumber(paymentDetails.cardNumber);
  const cardName = paymentDetails.cardName.trim();
  const expiryDate = normalizeExpiry(paymentDetails.expiryDate);

  if (!cardNumber) {
    errors.cardNumber = "Card number is required";
  } else if (!CARD_NUMBER_REGEX.test(cardNumber)) {
    errors.cardNumber = "Card number must be exactly 16 digits";
  }
  if (!cardName) {
    errors.cardName = "Cardholder name is required";
  }
  if (!expiryDate) {
    errors.expiryDate = "Expiry date is required";
  } else if (!EXPIRY_REGEX.test(expiryDate)) {
    errors.expiryDate = "Expiry date format must be MM/YY";
  } else if (!isExpiryFutureOrCurrent(expiryDate)) {
    errors.expiryDate = "Card expiry date is in the past";
  }
  return errors;
};

export default function BuyerProfile() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "My Profile | Balitah";

    let descriptionTag = document.querySelector('meta[name="description"]');
    if (!descriptionTag) {
      descriptionTag = document.createElement("meta");
      descriptionTag.setAttribute("name", "description");
      document.head.appendChild(descriptionTag);
    }

    descriptionTag.setAttribute(
      "content",
      "Manage your buyer profile on Balitah, a local Bruneian e-commerce platform.",
    );
  }, []);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileErrors, setProfileErrors] = useState({});

  const [addresses, setAddresses] = useState([]);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [addressForm, setAddressForm] = useState(emptyAddress);
  const [addressErrors, setAddressErrors] = useState({});

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(emptyCard);
  const [savedCard, setSavedCard] = useState(emptySavedCard);
  const [cardErrors, setCardErrors] = useState({});

  useEffect(() => {
    const loadProfile = async () => {
      const [meRes, addressRes] = await Promise.all([
        api.getMe(),
        api.getAddresses(),
      ]);

      if (meRes.success) {
        setForm({
          name: meRes.data.name || "",
          email: meRes.data.email || "",
          phone: meRes.data.phone || "",
          role: meRes.data.role || "",
        });
      } else {
        alert(meRes.message || "Failed to load profile");
      }

      if (addressRes.success) {
        setAddresses(addressRes.data.addresses || []);
      } else {
        alert(addressRes.message || "Failed to load addresses");
      }

      setLoading(false);
    };

    loadProfile();
  }, []);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    const errors = validateProfileForm(form);
    if (Object.keys(errors).length > 0) {
      setProfileErrors(errors);
      return;
    }

    setSaving(true);
    const res = await api.updateMe({
      name: form.name.trim(),
      phone: normalizePhone(form.phone),
    });
    setSaving(false);

    if (res.success) {
      alert("Profile updated");
    } else {
      alert(res.message || "Failed to update profile");
    }
  };

  const handleLogout = async () => {
    const res = await api.logout();
    if (res.success) {
      navigate("/login");
    } else {
      alert(res.message || "Failed to logout");
    }
  };

  const openAddAddressModal = () => {
    setEditingAddressId(null);
    setAddressForm(emptyAddress);
    setAddressErrors({});
    setShowAddressModal(true);
  };

  const openEditAddressModal = (addr) => {
    setEditingAddressId(addr._id);
    const streetParts = splitStreetAddress(addr.street);
    setAddressForm({
      label: addr.label || "Home",
      receiverName: addr.receiverName || "",
      phone: addr.phone || "",
      streetLine1: streetParts.streetLine1,
      streetLine2: streetParts.streetLine2,
      city: addr.city || "",
      state: addr.state || "",
      postalCode: addr.postalCode || "",
    });
    setAddressErrors({});
    setShowAddressModal(true);
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm("Are you sure you want to delete this address?")) {
      return;
    }
    try {
      const data = await api.deleteAddress(addressId);
      if (data.success) {
        setAddresses(data.data?.addresses || []);
        alert("Address deleted");
      } else {
        alert(data.message || "Failed to delete address");
      }
    } catch (err) {
      console.error("Failed to delete address:", err);
      alert("Failed to delete address");
    }
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    const errors = validateAddressForm(addressForm);
    if (Object.keys(errors).length > 0) {
      setAddressErrors(errors);
      return;
    }

    const normalizedAddress = {
      ...addressForm,
      label: addressForm.label.trim(),
      receiverName: addressForm.receiverName.trim(),
      phone: normalizePhone(addressForm.phone),
      street: composeStreetAddress(
        addressForm.streetLine1,
        addressForm.streetLine2,
      ),
      city: addressForm.city.trim(),
      state: addressForm.state.trim(),
      postalCode: addressForm.postalCode.trim(),
      country: LOCAL_COUNTRY,
    };

    if (editingAddressId) {
      try {
        const deleteData = await api.deleteAddress(editingAddressId);
        if (!deleteData.success) {
          alert(deleteData.message || "Failed to update address");
          return;
        }
      } catch (err) {
        console.error("Failed to update address:", err);
        alert("Failed to update address");
        return;
      }
    }

    const addRes = await api.addAddress(normalizedAddress);
    if (!addRes.success) {
      alert(addRes.message || "Failed to save address");
      return;
    }

    setAddresses(addRes.data?.addresses || []);
    setShowAddressModal(false);
    setEditingAddressId(null);
    setAddressForm(emptyAddress);
    alert(editingAddressId ? "Address updated" : "Address added");
  };

  const handleSaveCard = async (e) => {
    e.preventDefault();
    const errors = validateCardForm(paymentDetails);
    if (Object.keys(errors).length > 0) {
      setCardErrors(errors);
      return;
    }

    const normalizedCardNumber = normalizeCardNumber(paymentDetails.cardNumber);
    const payload = {
      paymentCard: {
        last4: normalizedCardNumber.slice(-4),
        cardName: paymentDetails.cardName.trim(),
        expiryDate: normalizeExpiry(paymentDetails.expiryDate),
      },
    };

    const res = await api.updateMe(payload);
    if (!res.success) {
      alert(res.message || "Failed to save card details");
      return;
    }

    const saved = res.data?.paymentCard || payload.paymentCard;

    setCardErrors({});
    setSavedCard({
      last4: saved.last4 || "",
      cardName: saved.cardName || "",
      expiryDate: saved.expiryDate || "",
    });
    setPaymentDetails({
      cardNumber: "",
      cardName: paymentDetails.cardName.trim(),
      expiryDate: normalizeExpiry(paymentDetails.expiryDate),
    });

    setShowPaymentModal(false);
    alert("Payment details saved");
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setCardErrors({});
    setPaymentDetails({
      cardNumber: "",
      cardName: savedCard.cardName || "",
      expiryDate: savedCard.expiryDate || "",
    });
  };

  const maskedCard = savedCard.last4 ? `**** **** **** ${savedCard.last4}` : "";

  return (
    <div className="bp-page">
      <Navbar />

      <div className="bp-container">
        {/* Page Header */}
        <div className="bp-header">
          <h1>My Profile</h1>
          <p>Manage your account settings and preferences</p>
        </div>

        {loading ? (
          <div className="bp-loading">
            <div className="bp-spinner"></div>
            <p>Loading profile...</p>
          </div>
        ) : (
          <div className="bp-content">
            {/* Profile Section */}
            <div className="bp-section">
              <div className="bp-section-header">
                <div className="bp-section-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <div>
                  <h2>Personal Information</h2>
                  <p>Update your personal details</p>
                </div>
              </div>

              <form className="bp-form" onSubmit={handleSaveProfile}>
                <div className="bp-form-grid">
                  <div className="bp-input-group">
                    <label htmlFor="buyer-name">Full Name</label>
                    <input
                      id="buyer-name"
                      type="text"
                      value={form.name}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, name: e.target.value }))
                      }
                      placeholder="Enter your full name"
                    />
                    {profileErrors.name && (
                      <span className="bp-error">{profileErrors.name}</span>
                    )}
                  </div>

                  <div className="bp-input-group">
                    <label htmlFor="buyer-email">Email</label>
                    <input
                      id="buyer-email"
                      type="email"
                      value={form.email}
                      readOnly
                      className="bp-readonly"
                    />
                    <span className="bp-hint">Email cannot be changed</span>
                  </div>

                  <div className="bp-input-group">
                    <label htmlFor="buyer-phone">Phone Number</label>
                    <input
                      id="buyer-phone"
                      type="tel"
                      value={form.phone}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          phone: normalizePhone(e.target.value),
                        }))
                      }
                      placeholder="+673 1234567"
                    />
                    {profileErrors.phone && (
                      <span className="bp-error">{profileErrors.phone}</span>
                    )}
                  </div>

                  <div className="bp-input-group">
                    <label htmlFor="buyer-role">Account Type</label>
                    <input
                      id="buyer-role"
                      type="text"
                      value={form.role}
                      readOnly
                      className="bp-readonly bp-role-badge"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="bp-btn bp-btn-primary"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="bp-btn-spinner"></span>
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </form>
            </div>

            {/* Addresses Section */}
            <div className="bp-section">
              <div className="bp-section-header">
                <div className="bp-section-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div>
                  <h2>Shipping Addresses</h2>
                  <p>Manage your delivery locations</p>
                </div>
              </div>

              <div className="bp-addresses">
                {addresses.length === 0 ? (
                  <div className="bp-empty-state">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    <p>No saved addresses yet</p>
                    <span>Add an address for faster checkout</span>
                  </div>
                ) : (
                  <div className="bp-address-grid">
                    {addresses.map((addr) => (
                      <div key={addr._id} className="bp-address-card">
                        <div className="bp-address-label">{addr.label}</div>
                        <div className="bp-address-content">
                          <p className="bp-address-name">{addr.receiverName}</p>
                          <p>{splitStreetAddress(addr.street).streetLine1}</p>
                          {splitStreetAddress(addr.street).streetLine2 && (
                            <p>{splitStreetAddress(addr.street).streetLine2}</p>
                          )}
                          <p>
                            {addr.city}, {addr.state} {addr.postalCode}
                          </p>
                          <p className="bp-address-phone">
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                            </svg>
                            {addr.phone}
                          </p>
                        </div>
                        <div className="bp-address-actions">
                          <button
                            type="button"
                            className="bp-btn-icon"
                            onClick={() => openEditAddressModal(addr)}
                            title="Edit"
                          >
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="bp-btn-icon bp-btn-danger"
                            onClick={() => handleDeleteAddress(addr._id)}
                            title="Delete"
                          >
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              <line x1="10" y1="11" x2="10" y2="17" />
                              <line x1="14" y1="11" x2="14" y2="17" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  className="bp-btn bp-btn-secondary"
                  onClick={openAddAddressModal}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Add New Address
                </button>
              </div>
            </div>

            {/* Logout Button */}
            <button
              type="button"
              className="bp-btn bp-btn-logout"
              onClick={handleLogout}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Sign Out
            </button>
          </div>
        )}
      </div>

      {/* Address Modal */}
      {showAddressModal && (
        <div
          className="bp-modal-overlay"
          onClick={() => setShowAddressModal(false)}
        >
          <div className="bp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bp-modal-header">
              <h3>{editingAddressId ? "Edit Address" : "Add New Address"}</h3>
              <button
                type="button"
                className="bp-modal-close"
                onClick={() => setShowAddressModal(false)}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSaveAddress} className="bp-modal-form">
              <div className="bp-input-group">
                <label>Label</label>
                <input
                  type="text"
                  value={addressForm.label}
                  onChange={(e) =>
                    setAddressForm((prev) => ({
                      ...prev,
                      label: e.target.value,
                    }))
                  }
                  placeholder="e.g., Home, Office"
                />
                {addressErrors.label && (
                  <span className="bp-error">{addressErrors.label}</span>
                )}
              </div>

              <div className="bp-input-group">
                <label>Receiver Name</label>
                <input
                  type="text"
                  value={addressForm.receiverName}
                  onChange={(e) =>
                    setAddressForm((prev) => ({
                      ...prev,
                      receiverName: e.target.value,
                    }))
                  }
                  placeholder="Full name of receiver"
                />
                {addressErrors.receiverName && (
                  <span className="bp-error">{addressErrors.receiverName}</span>
                )}
              </div>

              <div className="bp-input-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={addressForm.phone}
                  onChange={(e) =>
                    setAddressForm((prev) => ({
                      ...prev,
                      phone: normalizePhone(e.target.value),
                    }))
                  }
                  placeholder="+673 1234567"
                />
                {addressErrors.phone && (
                  <span className="bp-error">{addressErrors.phone}</span>
                )}
              </div>

              <div className="bp-input-group">
                <label>Street Address 1</label>
                <input
                  type="text"
                  value={addressForm.streetLine1}
                  onChange={(e) =>
                    setAddressForm((prev) => ({
                      ...prev,
                      streetLine1: e.target.value,
                    }))
                  }
                  placeholder="House number and street name"
                />
                {addressErrors.streetLine1 && (
                  <span className="bp-error">{addressErrors.streetLine1}</span>
                )}
              </div>

              <div className="bp-input-group">
                <label>
                  Street Address 2{" "}
                  <span className="bp-optional">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={addressForm.streetLine2}
                  onChange={(e) =>
                    setAddressForm((prev) => ({
                      ...prev,
                      streetLine2: e.target.value,
                    }))
                  }
                  placeholder="Apartment, suite, unit, etc."
                />
              </div>

              <div className="bp-form-row">
                <div className="bp-input-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={addressForm.city}
                    onChange={(e) =>
                      setAddressForm((prev) => ({
                        ...prev,
                        city: e.target.value,
                      }))
                    }
                    placeholder="City"
                  />
                  {addressErrors.city && (
                    <span className="bp-error">{addressErrors.city}</span>
                  )}
                </div>

                <div className="bp-input-group">
                  <label>State/District</label>
                  <select
                    value={addressForm.state}
                    onChange={(e) =>
                      setAddressForm((prev) => ({
                        ...prev,
                        state: e.target.value,
                      }))
                    }
                  >
                    <option value="">Select district</option>
                    {DISTRICT_OPTIONS.map((district) => (
                      <option key={district} value={district}>
                        {district}
                      </option>
                    ))}
                  </select>
                  {addressErrors.state && (
                    <span className="bp-error">{addressErrors.state}</span>
                  )}
                </div>
              </div>

              <div className="bp-input-group">
                <label>Postal Code</label>
                <input
                  type="text"
                  value={addressForm.postalCode}
                  onChange={(e) =>
                    setAddressForm((prev) => ({
                      ...prev,
                      postalCode: e.target.value,
                    }))
                  }
                  placeholder="Postal code"
                />
                {addressErrors.postalCode && (
                  <span className="bp-error">{addressErrors.postalCode}</span>
                )}
              </div>

              <div className="bp-modal-actions">
                <button
                  type="button"
                  className="bp-btn bp-btn-ghost"
                  onClick={() => setShowAddressModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="bp-btn bp-btn-primary">
                  {editingAddressId ? "Update Address" : "Save Address"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="bp-modal-overlay" onClick={closePaymentModal}>
          <div className="bp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bp-modal-header">
              <h3>{maskedCard ? "Update Card" : "Add Card"}</h3>
              <button
                type="button"
                className="bp-modal-close"
                onClick={closePaymentModal}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSaveCard} className="bp-modal-form">
              <div className="bp-input-group">
                <label>Card Number</label>
                <input
                  type="text"
                  placeholder="1234 5678 9012 3456"
                  maxLength="16"
                  autoComplete="cc-number"
                  value={paymentDetails.cardNumber}
                  onChange={(e) =>
                    setPaymentDetails((prev) => ({
                      ...prev,
                      cardNumber: normalizeCardNumber(e.target.value),
                    }))
                  }
                />
                {cardErrors.cardNumber && (
                  <span className="bp-error">{cardErrors.cardNumber}</span>
                )}
              </div>

              <div className="bp-input-group">
                <label>Cardholder Name</label>
                <input
                  type="text"
                  autoComplete="cc-name"
                  value={paymentDetails.cardName}
                  onChange={(e) =>
                    setPaymentDetails((prev) => ({
                      ...prev,
                      cardName: e.target.value,
                    }))
                  }
                  placeholder="Name on card"
                />
                {cardErrors.cardName && (
                  <span className="bp-error">{cardErrors.cardName}</span>
                )}
              </div>

              <div className="bp-input-group">
                <label>Expiry Date</label>
                <input
                  type="text"
                  placeholder="MM/YY"
                  maxLength="5"
                  autoComplete="cc-exp"
                  value={paymentDetails.expiryDate}
                  onChange={(e) =>
                    setPaymentDetails((prev) => ({
                      ...prev,
                      expiryDate: normalizeExpiry(e.target.value),
                    }))
                  }
                />
                {cardErrors.expiryDate && (
                  <span className="bp-error">{cardErrors.expiryDate}</span>
                )}
              </div>

              <div className="bp-modal-actions">
                <button
                  type="button"
                  className="bp-btn bp-btn-ghost"
                  onClick={closePaymentModal}
                >
                  Cancel
                </button>
                <button type="submit" className="bp-btn bp-btn-primary">
                  Save Card
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
